// Admin: cross-tenant requests list.
//
// Built on DataTable, so the columns, cell typography, filter chips and
// result count come from one place rather than being re-styled here. Columns
// follow the design (Web Portal v2, TABLES.a_requests): reference,
// customer → recipient, created, total, state.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { PortalShell } from '../../components/shared/PortalShell';
import { StatusPill } from '../../components/shared/primitives';
import { DataTable, type Column } from '../../components/shared/DataTable';
import { ADMIN_NAV } from '../_navs';
import { requests, type ServiceRequestWithItems } from '../../lib/api';
import { formatHtgMinor } from '../../lib/money';

type Row = ServiceRequestWithItems & { customer?: { id: number; email: string | null } };

/**
 * Chips, not a dropdown — a dropdown hides the option set.
 *
 * Grouped by what an operator is looking for rather than listing all fifteen
 * statuses: "is this being quoted" is a question someone asks,
 * `customer_approval_pending` is a schema detail.
 */
const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'quoting', label: 'Quoting' },
  { key: 'paid', label: 'Paid' },
  { key: 'delivering', label: 'Delivering' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'problem', label: 'Problem' },
];

const GROUPS: Record<string, string[]> = {
  quoting: ['submitted', 'quote_pending', 'quoted', 'customer_approval_pending', 'payment_pending'],
  paid: ['paid', 'assigned_to_merchant'],
  delivering: ['in_progress', 'ready_for_delivery', 'delivered'],
  confirmed: ['confirmed'],
  problem: ['disputed', 'refunded', 'cancelled', 'failed'],
};

export function AdminRequestsPage() {
  const q = useQuery({ queryKey: ['admin', 'requests'], queryFn: () => requests.list() });
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    let list: Row[] = q.data ?? [];

    if (filter !== 'all') {
      const allowed = GROUPS[filter] ?? [];
      list = list.filter((r) => allowed.includes(r.status));
    }

    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter((r) =>
        String(r.id).includes(s)
        || (r.recipient?.full_name ?? '').toLowerCase().includes(s)
        || (r.customer?.email ?? '').toLowerCase().includes(s)
        || (r.title ?? '').toLowerCase().includes(s)
        || (r.category?.label ?? '').toLowerCase().includes(s));
    }

    return [...list].sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? ''));
  }, [q.data, filter, search]);

  const columns: Column<Row>[] = [
    {
      key: 'ref', header: 'Reference', type: 'mono', width: '110px',
      value: (r) => `REQ-${String(r.id).padStart(4, '0')}`,
    },
    {
      key: 'who', header: 'Customer → recipient', type: 'strong', width: 'minmax(0,1.6fr)',
      value: (r) => `${r.customer?.email ?? `user #${r.customer_id}`} → ${r.recipient?.full_name ?? '—'}`,
    },
    {
      key: 'city', header: 'City', type: 'text', width: 'minmax(0,1fr)',
      value: (r) => r.city?.name ?? '—',
    },
    {
      key: 'created', header: 'Created', type: 'muted', width: '110px',
      value: (r) => (r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'),
    },
    {
      key: 'total', header: 'Cart', type: 'money', width: '120px',
      value: (r) => (r.cart_subtotal_htg_minor > 0 ? formatHtgMinor(r.cart_subtotal_htg_minor) : '—'),
    },
    {
      key: 'state', header: 'State', type: 'node', width: '140px',
      value: (r) => <StatusPill status={r.status} />,
    },
  ];

  return (
    <PortalShell nav={ADMIN_NAV} activeId="requests" title="Requests" subtitle="Every request in every market.">
      <div className="space-y-4">
        <div className="relative max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted)]" aria-hidden="true" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by id, customer, recipient, category…"
            aria-label="Search requests"
            className="w-full rounded-lg border border-[var(--color-border)] bg-white py-2 pl-9 pr-3 text-[13px] focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
        </div>

        <DataTable
          title="All requests"
          subtitle="Newest first."
          filters={FILTERS}
          activeFilter={filter}
          onFilter={setFilter}
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          loading={q.isLoading}
          empty={search || filter !== 'all' ? 'Nothing matches this filter.' : 'No requests yet.'}
        />
      </div>
    </PortalShell>
  );
}
